import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useExportAnalyticsToCsvMutation,
  useGetDeviceDetailsQuery,
} from 'src/backend/api';
import LoadingButton from 'src/components/LoadingButton';
import PageCard from 'src/components/PageCard';
import MeasuredValuesDialog from './MeasuredValuesDialog';

export type ExportAnalyticsToCsvRequest = {
  startDate: Date;
  endDate: Date;
  fields: string[];
  includeSapImport: boolean;
};

type Params = {
  id: string;
};

export default function DeviceDetailsTelemetryExport() {
  const { id } = useParams<Params>();
  const { data: deviceDetails } = useGetDeviceDetailsQuery(id ?? '');
  const [exportAnalyticsToCsv, { isLoading }] =
    useExportAnalyticsToCsvMutation();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [customDatesDisabled, setCustomDatesDisabled] = useState(true);
  const [includeSapImport, setIncludeSapImport] = useState(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [timeRange, setTimeRange] = useState<string | null>('28-d');

  const [startDate, setStartDate] = useState(dayjs().subtract(28, 'd'));
  const [endDate, setEndDate] = useState(dayjs());

  function setDates(timeAgo: string) {
    const [amount, unit] = timeAgo.split('-');
    const units: 'd' | 'h' = unit as 'd' | 'h';
    setEndDate(dayjs());
    setStartDate(dayjs().subtract(Number(amount), units));
  }

  const onTimeRangeButtonChanged = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
      if (newAlignment === null) return;
      setTimeRange(newAlignment);
      if (newAlignment === 'Custom') {
        setCustomDatesDisabled(false);
      } else {
        setCustomDatesDisabled(true);
        setDates(newAlignment);
      }
    },
    [],
  );

  const handleDialogClose = useCallback((fields: string[]) => {
    setIsDialogOpen(false);
    if (fields.length > 0) setSelectedFields(fields);
  }, []);

  const handleExport = useCallback(() => {
    const request: ExportAnalyticsToCsvRequest = {
      startDate: startDate.toDate(),
      endDate: endDate.toDate(),
      fields: selectedFields,
      includeSapImport,
    };
    exportAnalyticsToCsv({
      id: `${id}`,
      exportRequest: request,
      filename: `telemetry-${
        deviceDetails?.overview?.serialNumber ?? ''
      }-${id}-${dayjs().format('YYYY-MM-DD')}.csv`,
    });
  }, [
    exportAnalyticsToCsv,
    id,
    startDate,
    endDate,
    deviceDetails,
    includeSapImport,
    selectedFields,
  ]);

  return (
    <>
      <MeasuredValuesDialog
        isOpen={isDialogOpen}
        onlyTelemetry
        selectedFields={selectedFields}
        closeDialog={handleDialogClose}
      />
      <PageCard title="Telemetry Export" icon={<FileDownloadOutlinedIcon />}>
        <Stack spacing={2} sx={{ px: 4, pb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ToggleButtonGroup
              value={timeRange}
              exclusive
              onChange={onTimeRangeButtonChanged}
              aria-label="time range"
              disabled={isLoading}
            >
              <ToggleButton value="28-d">28 Days</ToggleButton>
              <ToggleButton value="7-d">7 Days</ToggleButton>
              <ToggleButton value="24-h">24 Hours</ToggleButton>
              <ToggleButton value="1-h">1 Hour</ToggleButton>
              <ToggleButton value="Custom">Custom</ToggleButton>
            </ToggleButtonGroup>
            <Collapse
              in={!customDatesDisabled}
              orientation="horizontal"
              unmountOnExit
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack direction="row" spacing={1}>
                  <DateTimePicker
                    disabled={isLoading}
                    label="Start date"
                    value={startDate}
                    onChange={(value) => value && setStartDate(value)}
                  />
                  <DateTimePicker
                    disabled={isLoading}
                    label="End date"
                    value={endDate}
                    onChange={(value) => value && setEndDate(value)}
                  />
                </Stack>
              </LocalizationProvider>
            </Collapse>
          </Stack>
          <Divider />
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button
              variant="outlined"
              disabled={isLoading}
              onClick={() => setIsDialogOpen(true)}
            >
              Select Fields
            </Button>
            {selectedFields.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedFields.map((f) => (
                  <Chip
                    key={f}
                    label={f}
                    size="small"
                    onDelete={() =>
                      setSelectedFields((prev) => prev.filter((x) => x !== f))
                    }
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No fields selected
              </Typography>
            )}
          </Stack>
          <Divider />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <FormControlLabel
              label="Include SAP import"
              control={
                <Checkbox
                  checked={includeSapImport}
                  disabled={isLoading}
                  onChange={(e) => setIncludeSapImport(e.target.checked)}
                />
              }
            />
            <LoadingButton
              variant="contained"
              loading={isLoading}
              disabled={selectedFields.length === 0}
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleExport}
            >
              Export
            </LoadingButton>
          </Stack>
        </Stack>
      </PageCard>
    </>
  );
}
